import React from "react";
import { STATUS_META, ENTITY_META } from "@/lib/constants";
import { Phone, Building2, Pencil, Trash2, StickyNote, MapPin, Hash } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

function formatNoteDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("tr-TR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return null;
  }
}

export default function MembersList({
  members,
  onEdit,
  onDelete,
  selectionMode = false,
  selectedIds,
  onToggleSelect,
  onOpenDetail,
  canWrite = true,
}) {
  if (members.length === 0) {
    return (
      <div className="bg-white dark:bg-neutral-900 border border-dashed border-neutral-300 dark:border-neutral-700 rounded-lg p-8 text-center">
        <div className="text-sm text-neutral-500 dark:text-neutral-400">Kriterlere uyan üye bulunamadı.</div>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3" data-testid="members-list">
      {members.map((m) => {
        const meta = STATUS_META[m.status] || STATUS_META.Beyaz;
        const em = ENTITY_META[m.entity_type] || ENTITY_META.Firma;
        const isSelected = selectedIds?.has(m.id);
        const phoneLines = (m.phone || "").split("\n").filter(Boolean);
        const firstPhone = phoneLines[0]?.split(" - ")[0]?.replace(/[^\d+]/g, "");
        const noteDate = formatNoteDate(m.notes_updated_at);
        return (
          <div
            key={m.id}
            className={`bg-white dark:bg-neutral-900 border border-l-4 ${meta.border} rounded-lg p-4 shadow-sm hover:shadow-md transition-all ${
              isSelected
                ? "ring-2 ring-neutral-900 dark:ring-neutral-100 border-neutral-900"
                : "border-neutral-200 dark:border-neutral-800"
            } cursor-pointer`}
            data-testid={`member-list-item-${m.id}`}
            onClick={() =>
              selectionMode ? onToggleSelect?.(m.id) : onOpenDetail?.(m)
            }
          >
            {/* Top row: order # + status badge */}
            <div className="flex flex-wrap items-center justify-between gap-1.5 mb-2">
              <div className="flex items-center gap-2 min-w-0 shrink-0">
                {selectionMode && (
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => onToggleSelect?.(m.id)}
                    onClick={(e) => e.stopPropagation()}
                    className="shrink-0"
                    data-testid={`select-checkbox-${m.id}`}
                  />
                )}
                {m.order_no != null && (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] font-mono font-bold uppercase tracking-wider bg-neutral-950 dark:bg-neutral-100 text-white dark:text-neutral-950 px-2 py-0.5 rounded"
                    data-testid={`list-order-${m.id}`}
                  >
                    <Hash className="h-2.5 w-2.5" />
                    {m.order_no}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0 ml-auto">
                <span
                  className={`text-[9.5px] font-semibold uppercase tracking-wider ${em.bg} ${em.text} px-1.5 py-0.5 rounded`}
                  title={`${em.long} — ${em.vote}`}
                  data-testid={`list-entity-${m.id}`}
                >
                  {em.short}
                </span>
                <span
                  className={`text-[10px] font-semibold uppercase tracking-wider ${meta.bg} ${meta.text} px-2 py-0.5 rounded`}
                >
                  {meta.short}
                </span>
              </div>
            </div>

            {/* Member name (Yetkililer) */}
            <div className="font-heading font-bold text-[15px] leading-tight text-neutral-950 dark:text-neutral-50 mb-1 line-clamp-2">
              {m.name}
            </div>

            {/* Company (Unvan) */}
            {m.company && m.company !== m.name && (
              <div className="flex items-start gap-1.5 text-xs text-neutral-700 dark:text-neutral-300 min-w-0 mb-2">
                <Building2 className="h-3 w-3 shrink-0 mt-0.5" />
                <span className="line-clamp-2 leading-snug">{m.company}</span>
              </div>
            )}

            {/* Sicil numbers */}
            {(m.member_no || m.trade_reg_no) && (
              <div className="flex flex-wrap items-center gap-1.5 mb-2" data-testid={`list-sicil-${m.id}`}>
                {m.member_no && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono font-medium bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 border border-neutral-200 dark:border-neutral-700 px-1.5 py-0.5 rounded">
                    Üye Sicil: {m.member_no}
                  </span>
                )}
                {m.trade_reg_no && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono font-medium bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 border border-neutral-200 dark:border-neutral-700 px-1.5 py-0.5 rounded">
                    Tic. Sicil: {m.trade_reg_no}
                  </span>
                )}
              </div>
            )}

            {m.address && (
              <div className="flex items-start gap-1.5 text-[11px] text-neutral-500 dark:text-neutral-400 min-w-0 mb-2">
                <MapPin className="h-3 w-3 shrink-0 mt-0.5" />
                <span className="line-clamp-2 leading-snug">{m.address}</span>
              </div>
            )}

            {m.notes && (
              <div className="mt-2 flex flex-col gap-1 text-xs text-neutral-600 dark:text-neutral-300 bg-neutral-50 dark:bg-neutral-800/60 border border-neutral-200 dark:border-neutral-700 rounded p-2">
                <div className="flex items-start gap-1.5">
                  <StickyNote className="h-3 w-3 mt-0.5 shrink-0 text-neutral-400" />
                  <p className="line-clamp-3">{m.notes}</p>
                </div>
                {noteDate && (
                  <span
                    className="self-end text-[10px] font-mono text-neutral-400 dark:text-neutral-500"
                    data-testid={`list-note-date-${m.id}`}
                  >
                    Son değişiklik: {noteDate}
                  </span>
                )}
              </div>
            )}

            <div className="mt-3 flex items-center justify-between border-t border-neutral-100 dark:border-neutral-800 pt-2">
              {firstPhone ? (
                <a
                  href={`tel:${firstPhone}`}
                  className="flex items-center gap-1.5 text-xs text-neutral-700 dark:text-neutral-300 hover:text-neutral-950 dark:hover:text-white transition-colors min-w-0"
                  data-testid={`list-phone-${m.id}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Phone className="h-3.5 w-3.5 shrink-0" />
                  <span className="font-mono truncate">{phoneLines[0]}</span>
                </a>
              ) : (
                <span className="text-xs text-neutral-400">—</span>
              )}
              {!selectionMode && canWrite && (
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8"
                    onClick={(e) => { e.stopPropagation(); onEdit(m); }}
                    data-testid={`list-edit-${m.id}`}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/40"
                    onClick={(e) => { e.stopPropagation(); onDelete(m); }}
                    data-testid={`list-delete-${m.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
